import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CanAccess,
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Navigate } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import type { CrmDataProvider } from "../providers/types";
import type { Project, Sale, SalesProject, TeamRosterEntry } from "../types";
import { LeadAssignmentChannels } from "./LeadAssignmentChannels";
import { deriveSelectableAdvisors } from "./leadAssignmentConfigRules";

const PAGE = { page: 1, perPage: 1000 };
const SORT = { field: "id", order: "ASC" as const };
const QUERY_KEY = "lead_assignment_config";

/**
 * Almikia Epic EL (TASK-008): "Asignación de leads" -- direccion-only (UI gate here, RLS on
 * the `lead_assignment_*` tables underneath). Per project: switch the automatic assignment
 * on/off, choose the fallback when nobody is available, pick which active advisors take
 * part, and manage the channel catalogue. It never shows rankings, counts of received leads
 * or comparisons between advisors (independence guide): only a neutral "chose blocks" hint.
 */
export const LeadAssignmentConfig = () => (
  <CanAccess
    resource="lead_assignment"
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <LeadAssignmentContent />
  </CanAccess>
);

LeadAssignmentConfig.path = "/lead_assignment";

const LeadAssignmentContent = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [chosenProject, setChosenProject] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const projects = useGetList<Project>("projects", {
    pagination: PAGE,
    sort: SORT,
  });
  const sales = useGetList<Sale>("sales", { pagination: PAGE, sort: SORT });
  const memberships = useGetList<SalesProject>("sales_projects", {
    pagination: PAGE,
    sort: SORT,
  });
  const roster = useGetList<TeamRosterEntry>("team_roster", {
    pagination: PAGE,
    sort: SORT,
  });
  const config = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      const [rules, eligible, blocks, channels] = await Promise.all([
        dataProvider.getLeadAssignmentRules(),
        dataProvider.getLeadAssignmentEligibleAdvisors(),
        dataProvider.getAdvisorAvailabilityBlocks(),
        dataProvider.getLeadAssignmentChannels(),
      ]);
      return { rules, eligible, blocks, channels };
    },
  });

  if (projects.error || sales.error || memberships.error || config.error) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => {
          projects.refetch();
          sales.refetch();
          memberships.refetch();
          config.refetch();
        }}
      />
    );
  }
  if (
    !projects.data ||
    !sales.data ||
    !memberships.data ||
    roster.isPending ||
    !config.data
  ) {
    return <ListCardsSkeleton rows={4} />;
  }

  const activeProjects = projects.data.filter((p) => !p.archived);
  const projectId: Identifier | undefined =
    activeProjects.find((p) => String(p.id) === chosenProject)?.id ??
    activeProjects[0]?.id;
  const rule = config.data.rules.find(
    (r) => String(r.project_id) === String(projectId),
  );
  const advisors =
    projectId == null
      ? []
      : deriveSelectableAdvisors({
          projectId,
          sales: sales.data,
          memberships: memberships.data,
          eligible: config.data.eligible,
          roster: roster.data ?? [],
          blocks: config.data.blocks,
        });

  /** Runs one write, refreshes the screen and reports the outcome in plain Spanish. */
  const save = async (write: () => Promise<unknown>) => {
    setSaving(true);
    try {
      await write();
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      notify("crm.leadAssignment.saved", { type: "success" });
    } catch {
      notify("crm.leadAssignment.save_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const saveRule = (patch: {
    is_enabled?: boolean;
    fallback_to_eligible?: boolean;
  }) => {
    if (projectId == null) return;
    return save(() =>
      dataProvider.saveLeadAssignmentRule({
        project_id: projectId,
        is_enabled: rule?.is_enabled ?? false,
        fallback_to_eligible: rule?.fallback_to_eligible ?? false,
        ...patch,
      }),
    );
  };

  return (
    <div className="mt-2 max-w-2xl mx-auto space-y-4 pb-8">
      <h1 className="px-1 text-xl font-semibold">
        {translate("crm.leadAssignment.title")}
      </h1>
      <p className="px-1 text-sm text-muted-foreground">
        {translate("crm.leadAssignment.intro")}
      </p>

      <Card>
        <CardContent className="space-y-4 p-4">
          {activeProjects.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {translate("crm.leadAssignment.no_projects")}
            </p>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="lead-assignment-project">
                  {translate("crm.leadAssignment.project_label")}
                </Label>
                <select
                  id="lead-assignment-project"
                  className="h-11 w-full rounded-md border bg-background px-3 text-sm"
                  value={projectId == null ? "" : String(projectId)}
                  onChange={(e) => setChosenProject(e.target.value)}
                >
                  {activeProjects.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <ToggleRow
                id="lead-assignment-enabled"
                label={translate("crm.leadAssignment.rule_label")}
                help={translate("crm.leadAssignment.rule_help")}
                checked={rule?.is_enabled ?? false}
                disabled={saving}
                onChange={(is_enabled) => saveRule({ is_enabled })}
              />
              <ToggleRow
                id="lead-assignment-fallback"
                label={translate("crm.leadAssignment.fallback_label")}
                help={translate("crm.leadAssignment.fallback_help")}
                checked={rule?.fallback_to_eligible ?? false}
                disabled={saving}
                onChange={(fallback_to_eligible) =>
                  saveRule({ fallback_to_eligible })
                }
              />
            </>
          )}
        </CardContent>
      </Card>

      {projectId != null ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            <h2 className="font-medium">
              {translate("crm.leadAssignment.advisors_title")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {translate("crm.leadAssignment.advisors_help")}
            </p>
            {advisors.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {translate("crm.leadAssignment.advisors_empty")}
              </p>
            ) : (
              <ul className="divide-y">
                {advisors.map((advisor) => (
                  <li
                    key={advisor.salesId}
                    className="flex items-center justify-between gap-3 py-3"
                  >
                    <div className="min-w-0">
                      <p className="font-medium">
                        {advisor.name}
                        {advisor.code ? (
                          <span className="ml-2 text-sm font-normal text-muted-foreground">
                            {translate("crm.leadAssignment.clave", {
                              code: advisor.code,
                            })}
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {advisor.unavailable
                          ? translate("crm.leadAssignment.unavailable")
                          : translate(
                              advisor.hasChosenBlocks
                                ? "crm.leadAssignment.blocks_chosen"
                                : "crm.leadAssignment.blocks_none",
                            )}
                      </p>
                    </div>
                    <Switch
                      aria-label={translate(
                        "crm.leadAssignment.advisor_toggle",
                        { name: advisor.name },
                      )}
                      checked={advisor.isEligible}
                      disabled={saving || advisor.unavailable !== null}
                      onCheckedChange={(checked) =>
                        save(() =>
                          dataProvider.setLeadAssignmentEligibility(
                            projectId,
                            advisor.salesId,
                            checked,
                          ),
                        )
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}

      <LeadAssignmentChannels
        channels={config.data.channels}
        saving={saving}
        save={save}
      />
    </div>
  );
};

type ToggleRowProps = {
  id: string;
  label: string;
  help: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
};

const ToggleRow = ({
  id,
  label,
  help,
  checked,
  disabled,
  onChange,
}: ToggleRowProps) => (
  <div className="flex items-start justify-between gap-3">
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <p className="text-sm text-muted-foreground">{help}</p>
    </div>
    <Switch
      id={id}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onChange}
    />
  </div>
);
